import React, { useMemo } from 'react';
import { useQuery } from 'react-query';

import classNames from 'classnames';
import styled from 'styled-components';

import { getShopperEmailOwners } from 'apis/ShopRequestAPI';
import SelectAdvancedFilter from 'components/lib/SelectAdvancedFilter';
import { useAppSelector } from 'store/hook';
import { SelectAdvancedFilterOptionType } from 'types/common';

import {
  SHOPPER_CONTACT_LABEL_FILTERS,
  SHOPPER_CONTACT_LINKED_REQUEST_FILTERS,
  SHOPPER_CONTACT_MAILBOX_STATUS_FILTERS,
} from '../constants';
import { useShopperEmailsContext } from './ShopperEmailsContext';
import { ShopperContactOwnerType } from '../types';

const Container = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
`;

const StyledFilter = styled(SelectAdvancedFilter)`
  margin-right: 5px;
`;

const Link = styled.span`
  cursor: pointer;
  font-size: 10px;
  color: var(--text-secondary);
`;

const ResetContainer = styled.div`
  display: flex;
  align-items: center;
  border-left: 1px solid var(--line-gray);
  margin-left: 10px;
  padding-left: 10px;
  height: 12px;
`;

const MAILBOX_STATUS_OPTIONS = [
  { value: SHOPPER_CONTACT_MAILBOX_STATUS_FILTERS.LINKED, label: 'Linked' },
  { value: SHOPPER_CONTACT_MAILBOX_STATUS_FILTERS.AUTHENTICATED, label: 'Authenticated' },
  { value: SHOPPER_CONTACT_MAILBOX_STATUS_FILTERS.NEEDS_RECONNECT, label: 'Needs reconnect' },
  { value: SHOPPER_CONTACT_MAILBOX_STATUS_FILTERS.UNLINKED, label: 'Unlinked' },
];

const LINKED_REQUEST_OPTIONS = [
  { value: SHOPPER_CONTACT_LINKED_REQUEST_FILTERS.HAS_LINKED, label: 'Has linked' },
  { value: SHOPPER_CONTACT_LINKED_REQUEST_FILTERS.NO_LINKED, label: 'No linked' },
];

const LABEL_OPTIONS = [
  { value: SHOPPER_CONTACT_LABEL_FILTERS.HAS_LABEL, label: 'Has label' },
  { value: SHOPPER_CONTACT_LABEL_FILTERS.NO_LABEL, label: 'No label' },
];

const ShopperEmailsFilters = () => {
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const { filters, setFilters } = useShopperEmailsContext();
  const showOwnerFilter = Boolean(currentUser?.isSuperuser);
  const { data: owners = [] } = useQuery<ShopperContactOwnerType[]>({
    queryKey: ['shopper-emails', 'owners'],
    queryFn: getShopperEmailOwners,
    enabled: showOwnerFilter,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  const ownerOptions = useMemo(
    () =>
      owners.map((owner) => {
        const ownerName = owner.full_name?.trim() || owner.email;
        return {
          value: owner.id,
          label: ownerName === owner.email ? owner.email : `${ownerName} (${owner.email})`,
        };
      }),
    [owners]
  );

  const resetFilters = () => {
    setFilters({
      mailboxStatus: [],
      linkedShopRequests: [],
      labelPresence: [],
      ownerIds: [],
    });
  };

  const handleSelectMailboxStatus = (values: SelectAdvancedFilterOptionType[]) => {
    setFilters({ mailboxStatus: values.map((item) => String(item.value)) });
  };

  const handleSelectLinkedRequests = (values: SelectAdvancedFilterOptionType[]) => {
    setFilters({ linkedShopRequests: values.map((item) => String(item.value)) });
  };

  const handleSelectLabelPresence = (values: SelectAdvancedFilterOptionType[]) => {
    setFilters({ labelPresence: values.map((item) => String(item.value)) });
  };

  const handleSelectOwners = (values: SelectAdvancedFilterOptionType[]) => {
    setFilters({ ownerIds: values.map((item) => Number(item.value)) });
  };

  return (
    <Container>
      <StyledFilter
        className={classNames({ active: filters.mailboxStatus.length > 0 })}
        options={MAILBOX_STATUS_OPTIONS}
        defaultValue={filters.mailboxStatus}
        onSelect={handleSelectMailboxStatus}
        placeholder="Mailbox"
        dataTestId="shopper-contact-filter-mailbox"
      />
      <StyledFilter
        className={classNames({ active: filters.linkedShopRequests.length > 0 })}
        options={LINKED_REQUEST_OPTIONS}
        defaultValue={filters.linkedShopRequests}
        onSelect={handleSelectLinkedRequests}
        placeholder="Linked Requests"
        dataTestId="shopper-contact-filter-linked-requests"
      />
      <StyledFilter
        className={classNames({ active: filters.labelPresence.length > 0 })}
        options={LABEL_OPTIONS}
        defaultValue={filters.labelPresence}
        onSelect={handleSelectLabelPresence}
        placeholder="Labels"
        dataTestId="shopper-contact-filter-labels"
      />
      {showOwnerFilter && (
        <StyledFilter
          className={classNames({ active: filters.ownerIds.length > 0 })}
          options={ownerOptions}
          defaultValue={filters.ownerIds}
          onSelect={handleSelectOwners}
          placeholder="Owner"
          dataTestId="shopper-contact-filter-owner"
        />
      )}
      <ResetContainer>
        <Link onClick={resetFilters}>Reset</Link>
      </ResetContainer>
    </Container>
  );
};

export default ShopperEmailsFilters;
